<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { vCardFilename } from './vcard.mjs';

const props = defineProps({
  cards: { type: Array, required: true },
});

const $store = useStore();
const { t } = useI18n();
const savingKey = ref('');
const savedKeys = ref(new Set());

const cardItems = computed(() =>
  props.cards.map((card, index) => ({
    ...card,
    key: `${card.phones[0]?.digits || card.emails[0] || card.name}-${index}`,
    initials: card.name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase())
      .join(''),
  }))
);

function filterPayload(attributeKey, value) {
  return {
    payload: [
      {
        attribute_key: attributeKey,
        filter_operator: 'equal_to',
        values: [value],
        attribute_model: 'standard',
        custom_attribute_type: '',
      },
    ],
  };
}

async function findExistingContact(card) {
  const phone = card.phones[0];
  const attributeKey = phone ? 'phone_number' : 'email';
  const value = phone?.digits || card.emails[0];
  if (!value) return null;

  const contacts = await $store.dispatch('contacts/filter', {
    queryPayload: filterPayload(attributeKey, value),
    resetState: false,
  });
  return contacts.shift() || null;
}

function contactPayload(card) {
  return {
    name: card.name,
    ...(card.phones[0]?.normalized
      ? { phone_number: card.phones[0].normalized }
      : {}),
    ...(card.emails[0] ? { email: card.emails[0] } : {}),
    additional_attributes: {
      ...(card.organization ? { company_name: card.organization } : {}),
      ...(card.title ? { description: card.title } : {}),
    },
  };
}

function downloadVCard(card) {
  const blob = new Blob([card.raw], { type: 'text/vcard;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = vCardFilename(card.name);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

async function saveContact(card) {
  if (savingKey.value) return;
  savingKey.value = card.key;
  downloadVCard(card);

  try {
    const existing = await findExistingContact(card);
    if (!existing) {
      await $store.dispatch('contacts/create', contactPayload(card));
    }
    savedKeys.value = new Set([...savedKeys.value, card.key]);
    useAlert('Kişi Seam-Chat’e kaydedildi; telefon rehberi için vCard indirildi.');
  } catch (error) {
    useAlert(error?.data || error?.message || t('CONTACT_FORM.ERROR_MESSAGE'));
  } finally {
    savingKey.value = '';
  }
}
</script>

<template>
  <div class="grid gap-2 min-w-72 max-w-sm">
    <article
      v-for="card in cardItems"
      :key="card.key"
      class="overflow-hidden rounded-xl border border-n-container bg-n-alpha-2"
    >
      <div class="flex items-center gap-3 p-3">
        <div
          class="grid place-content-center size-10 shrink-0 rounded-full bg-n-solid-blue text-white text-sm font-semibold"
        >
          {{ card.initials || 'WA' }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate font-medium text-n-slate-12">
            {{ card.name }}
          </div>
          <div
            v-if="card.organization || card.title"
            class="truncate text-xs text-n-slate-11"
          >
            {{ [card.title, card.organization].filter(Boolean).join(' · ') }}
          </div>
        </div>
      </div>

      <div class="grid gap-1 px-3 pb-3">
        <a
          v-for="phone in card.phones"
          :key="phone.digits"
          :href="`tel:${phone.normalized}`"
          class="skip-context-menu flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-3"
        >
          <Icon icon="i-lucide-phone" class="size-4 text-n-slate-11" />
          <span class="truncate">{{ phone.display }}</span>
          <span v-if="phone.type" class="ml-auto text-xs uppercase text-n-slate-10">
            {{ phone.type }}
          </span>
        </a>
        <a
          v-for="email in card.emails"
          :key="email"
          :href="`mailto:${email}`"
          class="skip-context-menu flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-3"
        >
          <Icon icon="i-lucide-mail" class="size-4 text-n-slate-11" />
          <span class="truncate">{{ email }}</span>
        </a>
      </div>

      <div class="grid grid-cols-[1fr_auto_auto] gap-1 border-t border-n-container p-2">
        <button
          type="button"
          class="skip-context-menu flex items-center justify-center gap-2 rounded-lg bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-solid-4 disabled:opacity-60"
          :disabled="Boolean(savingKey)"
          @click="saveContact(card)"
        >
          <Icon
            :icon="savedKeys.has(card.key) ? 'i-lucide-check' : 'i-lucide-user-plus'"
            class="size-4"
          />
          {{ savedKeys.has(card.key) ? 'Kaydedildi' : 'Kişiyi ekle' }}
        </button>
        <a
          v-if="card.phones[0]"
          :href="`https://wa.me/${card.phones[0].digits}`"
          target="_blank"
          rel="noreferrer noopener"
          title="WhatsApp’ta aç"
          aria-label="WhatsApp’ta aç"
          class="skip-context-menu grid size-9 place-content-center rounded-lg hover:bg-n-alpha-3"
        >
          <Icon icon="i-lucide-message-circle" class="size-4 text-n-slate-12" />
        </a>
        <button
          type="button"
          title="vCard indir"
          aria-label="vCard indir"
          class="skip-context-menu grid size-9 place-content-center rounded-lg hover:bg-n-alpha-3"
          @click="downloadVCard(card)"
        >
          <Icon icon="i-lucide-download" class="size-4 text-n-slate-12" />
        </button>
      </div>
    </article>
  </div>
</template>
